import { UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import { userAPI, userProjectAPI } from '../../services/api'
import { ProjectWithStats, User, UserProjectWithDetails } from '../../types'

interface AssignUsersModalProps {
  project: ProjectWithStats
  onClose: () => void
  onAssignComplete: () => void
}

export default function AssignUsersModal({
  project,
  onClose,
  onAssignComplete,
}: AssignUsersModalProps) {
  const [users, setUsers] = useState<User[]>([])
  const [assignedUserIds, setAssignedUserIds] = useState<Set<string>>(new Set())
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [project.id])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [usersRes, assignedRes] = await Promise.all([
        userAPI.list(),
        userProjectAPI.getProjectUsers(project.id),
      ])
      setUsers(usersRes.data)
      const assigned = (assignedRes.data as UserProjectWithDetails[]).map(
        up => up.user_id || (up.user?.id ?? '')
      )
      setAssignedUserIds(new Set(assigned))
      setSelectedUserIds(new Set(assigned))
    } catch (err: any) {
      console.error('Error fetching data:', err)
      setError(err.response?.data?.detail || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const handleAssign = async () => {
    try {
      setSubmitting(true)
      setError(null)
      const toAdd = [...selectedUserIds].filter(id => !assignedUserIds.has(id))
      const toRemove = [...assignedUserIds].filter(id => !selectedUserIds.has(id))

      if (toAdd.length > 0) {
        await userProjectAPI.assignUsers(project.id, toAdd)
      }
      for (const userId of toRemove) {
        await userProjectAPI.unassignUser(project.id, userId)
      }

      onAssignComplete()
      onClose()
    } catch (err: any) {
      console.error('Error assigning users:', err)
      setError(err.response?.data?.detail || 'Failed to assign users')
    } finally {
      setSubmitting(false)
    }
  }

  const hasChanges =
    selectedUserIds.size !== assignedUserIds.size ||
    [...selectedUserIds].some(id => !assignedUserIds.has(id)) ||
    [...assignedUserIds].some(id => !selectedUserIds.has(id))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <UserPlusIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Assign Users to Project</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            Assign users to <span className="font-medium text-gray-900">{project.name}</span>.
            Assigned users will see this project in &quot;My Projects&quot;.
          </p>
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-2">
              {users
                .filter(u => u.is_active)
                .map(user => {
                  const isAssigned = assignedUserIds.has(user.id)
                  const isSelected = selectedUserIds.has(user.id)
                  return (
                    <label
                      key={user.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUser(user.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{user.name}</div>
                        <div className="text-sm text-gray-500 truncate">{user.email}</div>
                      </div>
                      {isAssigned && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                          Assigned
                        </span>
                      )}
                    </label>
                  )
                })}
              {users.filter(u => u.is_active).length === 0 && (
                <p className="text-center text-gray-500 py-8">No users available</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={submitting || !hasChanges}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Assigning...' : 'Assign Users'}
          </button>
        </div>
      </div>
    </div>
  )
}
